/**
 * Beschwerdebilder – SEO-Themenseiten ("Beschwerden A–Z").
 * Jede Seite zielt auf konkrete Symptom-Suchanfragen ab und verlinkt
 * intern zu Leistungen und passenden Magazin-Artikeln.
 */

import { extraComplaints } from "./complaints.extra";
import { deepSections } from "./complaints.deep";


export interface ComplaintFaq {
  q: string;
  a: string;
}

export interface Complaint {
  slug: string;
  /** H1 der Seite */
  title: string;
  /** Kurzform für Kacheln/Navigation */
  shortTitle: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
  /** Synonyme & Suchbegriffe, die Menschen tatsächlich eingeben */
  synonyms: string[];
  intro: string;
  sections: { heading: string; paragraphs: string[] }[];
  signs: string[];
  approach: string[];
  faq: ComplaintFaq[];
  /** Slugs passender Magazin-Artikel */
  relatedPosts: string[];
  /** Anker auf der Startseite */
  serviceAnchor: string;
}

const baseComplaints: Complaint[] = [
  {
    slug: "reizdarm-blaehbauch",
    title: "Reizdarm, Blähbauch und anhaltende Verdauungsbeschwerden",
    shortTitle: "Reizdarm & Blähbauch",
    metaTitle: "Reizdarm & Blähbauch: Ursachen finden statt Symptome dämpfen | Praxis IB Wien",
    metaDescription:
      "Reizdarm, Blähbauch, Krämpfe, wechselnder Stuhlgang: mikrobiologische Diagnostik und individuelle komplementärmedizinische Begleitung in Wien.",
    keywords: [
      "Reizdarm Wien",
      "Blähbauch Ursachen",
      "Reizdarmsyndrom Therapie",
      "Darmbeschwerden komplementärmedizinisch",
      "Mikrobiom Analyse Wien",
    ],
    synonyms: [
      "Reizdarmsyndrom (RDS/IBS)",
      "aufgeblähter Bauch nach dem Essen",
      "Bauchkrämpfe ohne Befund",
      "wechselnd Durchfall und Verstopfung",
      "Nahrungsmittel werden schlechter vertragen",
    ],
    intro:
      "Ein Reizdarm ist keine Einbildung und keine Diagnose zweiter Klasse. Er beschreibt einen Zustand, in dem Darmbewegung, Schleimhaut, Mikrobiota und Nervensystem nicht mehr gut zusammenspielen. Genau deshalb bringt die Frage „Was fehlt im Befund?“ oft weniger als die Frage „Welche Regelkreise sind aus dem Takt?“.",
    sections: [
      {
        heading: "Warum ein unauffälliger Befund die Beschwerden nicht widerlegt",
        paragraphs: [
          "Koloskopie, Blutbild und Ultraschall schließen strukturelle Erkrankungen aus – das ist wichtig und immer der erste Schritt. Sie bilden aber weder die Zusammensetzung der Darmflora noch die Barrierefunktion der Schleimhaut oder die Aktivität des darmassoziierten Immunsystems ab.",
          "Zwischen „organisch gesund“ und „beschwerdefrei“ liegt ein großer funktioneller Raum. In diesem Raum bewegt sich komplementärmedizinische Arbeit: nicht anstelle der Schulmedizin, sondern als Ergänzung dort, wo Standarddiagnostik keine Antwort gibt.",
        ],
      },
      {
        heading: "Was mikrobiologisch dahinterstehen kann",
        paragraphs: [
          "Eine verringerte Vielfalt der Darmflora, ein Mangel an schleimhautschützenden Bakterien wie Akkermansia muciniphila oder Faecalibacterium prausnitzii, eine Fehlbesiedlung des Dünndarms, eine gestörte Gallensäure- oder Kohlenhydratverwertung sowie Reste nach Antibiotikatherapien sind häufige Mitverursacher.",
          "Kurzkettige Fettsäuren – vor allem Butyrat – sind der Hauptenergielieferant der Dickdarmschleimhaut. Fehlen die Bakterien, die sie bilden, leidet die Barriere, und der Darm reagiert empfindlicher auf Dehnung, Gasbildung und Stress.",
        ],
      },
      {
        heading: "Die Darm-Hirn-Achse ernst nehmen",
        paragraphs: [
          "Darm und Gehirn kommunizieren ununterbrochen über Vagusnerv, Immunbotenstoffe und mikrobielle Metabolite. Anhaltender Stress verändert Motilität, Durchblutung und Schmerzwahrnehmung messbar – und ein gereizter Darm wiederum verändert Stimmung und Belastbarkeit. Beide Richtungen gehören in die Betrachtung.",
        ],
      },
      {
        heading: "Was realistisch erreichbar ist",
        paragraphs: [
          "Der Reizdarm gehört zu jenen Beschwerdebildern, bei denen sich mit konsequenter Arbeit erfahrungsgemäß sehr viel bewegen lässt. In der überwiegenden Zahl der Verläufe berichten Betroffene nach einigen Monaten über deutlich weniger Blähungen, ruhigere Verdauung, eine wieder größere Auswahl an verträglichen Lebensmitteln – und vor allem darüber, den Tag nicht mehr um den Darm herum planen zu müssen.",
          "Beschwerdefreiheit über längere Zeiträume ist keine Ausnahme, sondern ein realistisches Ziel. Auch wenn sensible Phasen bleiben können: Sie lernen, sie einzuordnen und früh gegenzusteuern. Das ist der Unterschied zwischen Ausgeliefertsein und Handlungsfähigkeit – und er ist in aller Regel erreichbar.",
        ],
      },
    ],
    signs: [
      "Blähbauch, der im Tagesverlauf zunimmt",
      "Krämpfe, Druckgefühl oder Völlegefühl nach dem Essen",
      "wechselnde Stuhlkonsistenz ohne erkennbares Muster",
      "zunehmende Unverträglichkeit einzelner Lebensmittel",
      "Beschwerden, die sich unter Stress deutlich verstärken",
    ],
    approach: [
      "ausführliche Anamnese inklusive Vorbefunden, Medikation und Krankheitsverlauf",
      "mikrobiologische Stuhldiagnostik über Speziallabore (u. a. Enterosan, MicroGenDX)",
      "Beurteilung von Barriere-, Entzündungs- und Verdauungsmarkern",
      "individueller Therapieplan mit Mikronährstoffen, Phytotherapeutika und Ernährungsstrategie",
      "schrittweise Anpassung anhand von Verlaufskontrollen",
    ],
    faq: [
      {
        q: "Ist Reizdarm heilbar?",
        a: "Reizdarm ist eine funktionelle Störung, kein unveränderliches Schicksal. Wenn die beteiligten Faktoren – Mikrobiota, Schleimhaut, Ernährung, Stressachse – gezielt bearbeitet werden, bessern sich Beschwerden in vielen Fällen deutlich. Ein Heilungsversprechen gibt es dennoch nicht.",
      },
      {
        q: "Brauche ich eine Stuhlanalyse?",
        a: "Nicht immer. Sinnvoll ist sie, wenn die Beschwerden anhalten, mehrere Ernährungsumstellungen erfolglos blieben oder Vorgeschichte und Symptomatik auf eine Dysbiose hinweisen. Welche Analyse passt, entscheide ich nach der Anamnese.",
      },
      {
        q: "Muss ich dauerhaft Lebensmittel weglassen?",
        a: "Restriktive Diäten können kurzfristig entlasten, verringern langfristig aber die Vielfalt der Darmflora. Ziel ist deshalb immer, die Toleranz wieder aufzubauen statt den Speiseplan dauerhaft zu verkleinern.",
      },
    ],
    relatedPosts: [
      "sibo-pathophysiologie-diagnostik-therapie",
      "akkermansia-muciniphila-faecalibacterium-prausnitzii-darmflora-schleimhaut",
      "die-nadel-im-heuhaufen-das-mikrobiom-und-sein-einfluss",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "sibo-duenndarmfehlbesiedlung",
    title: "SIBO – Dünndarmfehlbesiedlung erkennen und behandeln",
    shortTitle: "SIBO / Dünndarmfehlbesiedlung",
    metaTitle: "SIBO: Dünndarmfehlbesiedlung – Diagnostik & Therapie | Praxis IB Wien",
    metaDescription:
      "SIBO verstehen: Atemtest, Wasserstoff und Methan, Rezidivneigung und was nach der Sanierung wirklich zählt. Komplementärmedizinische Begleitung in Wien.",
    keywords: [
      "SIBO Wien",
      "Dünndarmfehlbesiedlung Symptome",
      "SIBO Atemtest",
      "Methan SIBO Verstopfung",
      "SIBO Rezidiv",
    ],
    synonyms: [
      "Blähungen direkt nach dem Essen",
      "Bauch wird im Lauf des Tages immer dicker",
      "Besserung durch Antibiotika, danach Rückfall",
      "Unverträglichkeit von Ballaststoffen und Probiotika",
    ],
    intro:
      "Bei einer Dünndarmfehlbesiedlung siedeln sich Bakterien dort an, wo sie in dieser Menge nicht hingehören: im oberen Dünndarm. Sie vergären Kohlenhydrate, bevor der Körper sie aufnehmen kann – mit Gasbildung, Druck und häufig auch Nährstoffdefiziten als Folge.",
    sections: [
      {
        heading: "Warum SIBO so oft wiederkommt",
        paragraphs: [
          "Eine antibiotische oder pflanzliche Sanierung reduziert die Keimlast, verändert aber nicht automatisch die Ursache. Bleiben eine träge Dünndarmmotilität, ein Mangel an Magensäure oder Gallensäuren, Verwachsungen oder eine Grunderkrankung unberücksichtigt, kehrt die Fehlbesiedlung meist zurück.",
          "Deshalb gehört zu jeder SIBO-Behandlung eine zweite Phase: die Wiederherstellung von Motilität, Verdauungsleistung und Schleimhautschutz.",
        ],
      },
      {
        heading: "Wasserstoff, Methan, Schwefelwasserstoff",
        paragraphs: [
          "Wasserstoffdominante Formen gehen häufiger mit Durchfall einher, methandominante (korrekt: intestinale methanogene Überwucherung) eher mit Verstopfung und starkem Blähbauch. Die Unterscheidung verändert die Therapieauswahl erheblich – eine pauschale „SIBO-Kur“ gibt es nicht.",
        ],
      },
      {
        heading: "Wie gut die Aussichten sind",
        paragraphs: [
          "SIBO hat den Ruf, hartnäckig zu sein – zu Recht, wenn nur die Bakterien reduziert und die Ursache übersehen wird. Wird beides bearbeitet, sieht das Bild deutlich freundlicher aus: Motilität, Gallensäuren, Verdauungsleistung und Ernährungsmuster gemeinsam anzugehen führt in vielen Fällen zu stabiler Beschwerdefreiheit.",
          "Rückfälle sind möglich, aber sie sind kein Scheitern und meist Hinweis auf einen noch offenen Faktor. Die meisten Menschen erreichen mit einem geduldigen, gestuften Vorgehen eine sehr gute Lebensqualität und eine wieder breite, entspannte Ernährung.",
        ],
      },
    ],
    signs: [
      "Blähungen innerhalb von 30 bis 90 Minuten nach der Mahlzeit",
      "Verschlechterung durch Ballaststoffe, Präbiotika oder Probiotika",
      "Eisen- oder Vitamin-B12-Mangel ohne erkennbare Ursache",
      "kurzfristige Besserung durch Antibiotika mit anschließendem Rückfall",
    ],
    approach: [
      "Auswertung vorhandener Atemtests und Vorbefunde",
      "Differenzierung zwischen wasserstoff- und methandominanten Verläufen",
      "phytotherapeutische Strategien und gezielte Mikronährstoffe",
      "Aufbau von Motilität, Verdauungsleistung und Schleimhautbarriere",
      "Rezidivprophylaxe mit realistischem Ernährungsrahmen",
    ],
    faq: [
      {
        q: "Ist ein Atemtest zwingend notwendig?",
        a: "Er ist hilfreich, aber nicht unfehlbar. Falsch negative Ergebnisse kommen vor. Anamnese, Beschwerdemuster und Verlauf sind ebenso aussagekräftig und werden gemeinsam bewertet.",
      },
      {
        q: "Wie lange dauert eine Begleitung?",
        a: "Realistisch sind mehrere Monate, da nach der Reduktionsphase der Wiederaufbau folgt. Kurzfristige Kuren allein führen erfahrungsgemäß zu Rückfällen.",
      },
    ],
    relatedPosts: [
      "sibo-pathophysiologie-diagnostik-therapie",
      "die-nadel-im-heuhaufen-das-mikrobiom-und-sein-einfluss",
      "quorum-sensing-ein-ueberblick-ueber-mechanismen-bedeutung",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "chronische-blasenentzuendung",
    title: "Chronische Blasenentzündung, interstitielle Zystitis und Beckenschmerz",
    shortTitle: "Blase & Beckenschmerz",
    metaTitle: "Chronische Blasenentzündung & interstitielle Zystitis | Praxis IB Wien",
    metaDescription:
      "Wiederkehrende Harnwegsinfekte, interstitielle Zystitis und CPPS: Biofilme, Urinmikrobiom und erweiterte DNA-Diagnostik verständlich erklärt.",
    keywords: [
      "chronische Blasenentzündung Wien",
      "interstitielle Zystitis",
      "Painful Bladder Syndrome",
      "wiederkehrende Harnwegsinfekte",
      "CPPS Beckenschmerz",
    ],
    synonyms: [
      "Blasenentzündung kommt immer wieder",
      "Harnkultur negativ, Beschwerden bleiben",
      "ständiger Harndrang ohne Infekt",
      "Brennen und Druck im Unterbauch",
    ],
    intro:
      "Wenn Blasenbeschwerden immer wiederkehren oder dauerhaft bestehen, obwohl die Harnkultur unauffällig ist, endet für viele Betroffene die medizinische Erklärung – nicht aber das Leiden. Genau hier lohnt der Blick auf Biofilme, das Urinmikrobiom und die Schleimhautbarriere.",
    sections: [
      {
        heading: "Warum die klassische Harnkultur oft nichts findet",
        paragraphs: [
          "Die Standardkultur ist auf wenige, schnell wachsende Erreger ausgelegt. Bakterien, die in Biofilmen oder intrazellulär in der Blasenwand persistieren, entziehen sich ihr weitgehend. Ein negativer Befund bedeutet daher nicht automatisch, dass keine mikrobielle Beteiligung vorliegt.",
          "DNA-basierte Verfahren wie qPCR und Next-Generation-Sequencing können deutlich mehr Spezies erfassen – inklusive anaerober und schwer kultivierbarer Keime.",
        ],
      },
      {
        heading: "Biofilme als Grund für Rezidive",
        paragraphs: [
          "In einem Biofilm sind Bakterien von einer schützenden Matrix umgeben, kommunizieren über Quorum Sensing und sind gegenüber Antibiotika um ein Vielfaches unempfindlicher. Klingt der Infekt unter Therapie zunächst ab und kehrt Wochen später zurück, ist das ein typisches Muster.",
        ],
      },
      {
        heading: "Wenn kein Erreger beteiligt ist",
        paragraphs: [
          "Bei interstitieller Zystitis und chronischem Beckenschmerzsyndrom stehen eine geschädigte Glykosaminoglykan-Schicht, Mastzellaktivierung, muskuläre Verspannung des Beckenbodens und eine sensibilisierte Schmerzverarbeitung im Vordergrund. Auch diese Ebenen lassen sich gezielt unterstützen.",
        ],
      },
      {
        heading: "Warum ich hier zuversichtlich bin",
        paragraphs: [
          "Gerade bei wiederkehrenden Blasen- und Unterbauchbeschwerden erlebe ich immer wieder Verläufe, die vorher niemand für möglich gehalten hätte: längere infektfreie Intervalle, seltener Antibiotika, deutlich weniger Schmerz und Harndrang – und irgendwann Monate, in denen die Blase schlicht kein Thema mehr ist.",
          "Wenn Biofilme, Mikrobiom, Barriere und Entzündungslast konsequent bearbeitet werden, ist eine anhaltende Beschwerdearmut in vielen Fällen realistisch. Auch bei langen Vorgeschichten. Bitte lassen Sie sich von der Dauer Ihrer Geschichte nicht davon abbringen, es noch einmal strukturiert anzugehen.",
        ],
      },
    ],
    signs: [
      "mehr als drei Harnwegsinfekte pro Jahr",
      "Beschwerden trotz negativer Harnkultur",
      "Brennen, Druck oder Schmerz im Unterbauch und Beckenboden",
      "häufiger Harndrang, besonders nachts",
      "Verschlechterung durch Stress, Zyklus oder bestimmte Lebensmittel",
    ],
    approach: [
      "systematische Aufarbeitung der bisherigen Befunde und Antibiotikaverläufe",
      "erweiterte mikrobiologische Diagnostik inklusive DNA-basierter Verfahren",
      "biofilmwirksame phytotherapeutische Konzepte",
      "Aufbau von Schleimhautschutz und Urogenitalflora",
      "Begleitung der Schmerz- und Stressachse",
    ],
    faq: [
      {
        q: "Ersetzt das die urologische Behandlung?",
        a: "Nein. Die urologische Abklärung bleibt die Basis. Meine Arbeit setzt ergänzend dort an, wo Rezidive trotz leitliniengerechter Therapie bestehen bleiben.",
      },
      {
        q: "Wie lange dauert es, bis sich etwas ändert?",
        a: "Bei chronischen Verläufen sind erste Veränderungen meist nach acht bis zwölf Wochen spürbar. Biofilmassoziierte Verläufe brauchen länger und einen ruhigen, konsequenten Plan.",
      },
    ],
    relatedPosts: [
      "interstitielle-zystitis-painful-bladder-syndrome-cpps",
      "chronische-blaseninfektionen-mikroben-biofilme-und-innovative-therapeutische-ansaetze",
      "die-rolle-von-biofilmen-in-der-symbiose-zwischen-nuetzlichen-und-pathogenen-mikroben",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "haut-akne-ekzeme",
    title: "Hautprobleme: Akne, Ekzeme und die Darm-Haut-Achse",
    shortTitle: "Haut & Darm-Haut-Achse",
    metaTitle: "Akne, Ekzeme & Darm-Haut-Achse: Ursachen verstehen | Praxis IB Wien",
    metaDescription:
      "Warum Hautbeschwerden oft im Darm mitbegründet sind: Darm-Haut-Achse, Mikrobiom, Talgregulation und Entzündung – komplementärmedizinisch begleitet in Wien.",
    keywords: [
      "Darm Haut Achse",
      "Akne Darm Mikrobiom",
      "Ekzem Neurodermitis Darm",
      "Rosacea komplementär",
      "Hautprobleme Ursachen Wien",
    ],
    synonyms: [
      "unreine Haut trotz Pflege",
      "Neurodermitis-Schübe",
      "Rosacea und Rötungen",
      "Haut reagiert auf Stress und Ernährung",
    ],
    intro:
      "Haut und Darm teilen sich Barrierefunktion, Immunzellen und ein eigenes Mikrobiom. Deshalb ist die Haut häufig das sichtbare Ende eines Geschehens, das weiter innen beginnt.",
    sections: [
      {
        heading: "Wie die Darm-Haut-Achse funktioniert",
        paragraphs: [
          "Kurzkettige Fettsäuren aus der Darmflora wirken entzündungsregulierend und beeinflussen die Reifung von T-Zellen. Fehlen sie, verschiebt sich das Immunmilieu Richtung Entzündung – und die Haut reagiert mit Rötung, Schüben oder verzögerter Heilung.",
          "Gleichzeitig beeinflussen Blutzuckerschwankungen, Insulin und IGF-1 die Talgproduktion. Ernährung wirkt hier also nicht über Mythen, sondern über nachvollziehbare Signalwege.",
        ],
      },
      {
        heading: "Nach Antibiotika oder Isotretinoin",
        paragraphs: [
          "Viele Hautverläufe wurden über Jahre antibiotisch behandelt. Das hinterlässt Spuren in der Darmflora, die sich nicht von selbst korrigieren. Ein gezielter Wiederaufbau ist deshalb oft der entscheidende Schritt, um Rückfälle zu vermeiden.",
        ],
      },
      {
        heading: "Was Sie erwarten dürfen",
        paragraphs: [
          "Haut braucht Zeit, weil sie sich in Zyklen erneuert – doch genau deshalb ist sie auch ein so dankbares Organ: Was innen ruhiger wird, zeigt sich außen. Erfahrungsgemäß sehen die meisten Menschen nach zwei bis drei Monaten weniger Entzündungsherde, ruhigere Schübe und eine deutlich belastbarere Hautbarriere.",
          "Längere erscheinungsfreie Phasen sind in vielen Fällen erreichbar. Und mit ihnen kehrt etwas zurück, das Hauterkrankungen besonders gründlich nehmen: das unbefangene Gefühl, sich zeigen zu können.",
        ],
      },
    ],
    signs: [
      "Hautbeschwerden zusammen mit Verdauungsbeschwerden",
      "Schübe nach Stress, Zucker oder Milchprodukten",
      "wiederkehrende Verläufe nach Antibiotikatherapien",
      "trockene, empfindliche Haut mit gestörter Barriere",
    ],
    approach: [
      "gemeinsame Betrachtung von Haut, Darm und Immunlage",
      "mikrobiologische Diagnostik bei begleitenden Verdauungsbeschwerden",
      "Mikronährstoffe für Barriere und Wundheilung (u. a. Zink, Vitamin A, Omega-3)",
      "phytotherapeutische Begleitung entzündlicher Prozesse",
      "ruhige, alltagstaugliche Ernährungsanpassung ohne Verbotslisten",
    ],
    faq: [
      {
        q: "Ist Akne wirklich eine Darmfrage?",
        a: "Nicht ausschließlich. Hormone, Genetik und Talgdrüsenaktivität spielen mit. Der Darm ist jedoch ein Faktor, der sich gut beeinflussen lässt – und oft der übersehene.",
      },
      {
        q: "Wie lange dauert es, bis man Veränderungen sieht?",
        a: "Die Hauterneuerung braucht Zeit. Realistisch sind zwei bis drei Monate, bevor sich ein stabiler Trend zeigt.",
      },
    ],
    relatedPosts: [
      "neurodermitis-psoriasis-mikrobiom",
      "akkermansia-muciniphila-faecalibacterium-prausnitzii-darmflora-schleimhaut",
      "mikrobielles-immunsystem-und-dessen-einfluss-auf-die-immunantwort",
      "naturheilkunde-phytotherapie-biochemie-koerper",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "histaminintoleranz-unvertraeglichkeiten",
    title: "Histaminintoleranz und Nahrungsmittelunverträglichkeiten",
    shortTitle: "Histamin & Unverträglichkeiten",
    metaTitle: "Histaminintoleranz & Unverträglichkeiten verstehen | Praxis IB Wien",
    metaDescription:
      "Histamin, DAO, Fruktose- und Laktoseintoleranz: warum Unverträglichkeiten oft Folge und nicht Ursache sind – komplementärmedizinische Begleitung in Wien.",
    keywords: [
      "Histaminintoleranz Wien",
      "DAO Mangel",
      "Fruktoseintoleranz",
      "Nahrungsmittelunverträglichkeit Test",
      "Mastzellaktivierung",
    ],
    synonyms: [
      "Rötung, Herzrasen oder Kopfschmerz nach dem Essen",
      "immer mehr Lebensmittel werden nicht vertragen",
      "Reaktion auf Wein, Käse, Tomaten",
      "Juckreiz oder Quaddeln ohne Allergienachweis",
    ],
    intro:
      "Wer immer mehr Lebensmittel streichen muss, hat selten ein Lebensmittelproblem. Meist hat sich die Toleranz verringert – durch eine gestörte Schleimhaut, eine veränderte Mikrobiota oder ein überreiztes Immunsystem.",
    sections: [
      {
        heading: "Histamin ist eine Bilanz, keine Zutat",
        paragraphs: [
          "Entscheidend ist das Verhältnis aus zugeführtem Histamin, körpereigener Freisetzung durch Mastzellen und Abbaukapazität über Diaminoxidase (DAO) und HNMT. Auch bakterielle Histaminbildner im Darm tragen zur Gesamtlast bei.",
          "Deshalb kann derselbe Teller an einem Tag gut vertragen werden und an einem anderen nicht – abhängig von Stress, Zyklus, Schlaf und Infektlage.",
        ],
      },
      {
        heading: "Warum Auslassdiäten allein selten reichen",
        paragraphs: [
          "Karenz verschafft Ruhe und ist als Zwischenschritt sinnvoll. Bleibt sie die einzige Maßnahme, sinkt die Vielfalt der Darmflora weiter und die Toleranz nimmt langfristig ab. Ziel ist deshalb der schrittweise Wiederaufbau.",
        ],
      },
      {
        heading: "Die Toleranz kann zurückkommen",
        paragraphs: [
          "Unverträglichkeiten fühlen sich endgültig an – sie sind es in den seltensten Fällen. Da Histaminabbau, Schleimhautzustand, Mikrobiota und Mikronährstoffversorgung eng zusammenhängen, verbessert sich die Verträglichkeit sehr häufig, sobald diese Grundlagen stimmen.",
          "Ziel ist ausdrücklich nicht die perfekt geführte Verzichtsliste, sondern ein Alltag, in dem Sie wieder essen gehen, verreisen und spontan sein können. In den meisten Verläufen ist genau das erreichbar – oft mit deutlich mehr Spielraum, als anfangs für möglich gehalten wird.",
        ],
      },
    ],
    signs: [
      "Beschwerden 20 bis 60 Minuten nach dem Essen",
      "Kopfschmerz, Flush, Herzklopfen oder Nasenlaufen ohne Erkältung",
      "wachsende Liste unverträglicher Lebensmittel",
      "gleichzeitig bestehende Verdauungsbeschwerden",
    ],
    approach: [
      "Symptom- und Ernährungsprotokoll statt pauschaler Verdachtstests",
      "Beurteilung von Darmbarriere, Mikrobiota und Entzündungsmarkern",
      "gezielte Mikronährstoffe für Abbauwege und Mastzellstabilität",
      "strukturierter Wiederaufbau der Toleranz",
    ],
    faq: [
      {
        q: "Sind IgG-Tests auf Nahrungsmittel sinnvoll?",
        a: "Als alleinige Entscheidungsgrundlage nein. IgG-Antikörper zeigen in erster Linie Kontakt an, nicht Unverträglichkeit. Aussagekräftiger sind Verlauf, Protokoll und gezielte Provokation.",
      },
      {
        q: "Muss ich Histamin dauerhaft meiden?",
        a: "In den meisten Fällen nicht. Wenn Barriere und Abbaukapazität wieder besser arbeiten, steigt die verträgliche Menge in der Regel spürbar an.",
      },
    ],
    relatedPosts: [
      "neurodermitis-psoriasis-mikrobiom",
      "die-nadel-im-heuhaufen-das-mikrobiom-und-sein-einfluss",
      "mikrobielles-immunsystem-und-dessen-einfluss-auf-die-immunantwort",
      "blutwerte-sinnvoll-labordiagnostik-leitfaden",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "wiederkehrende-infekte-immunsystem",
    title: "Wiederkehrende Infekte und ein aus dem Takt geratenes Immunsystem",
    shortTitle: "Infektanfälligkeit & Immunsystem",
    metaTitle: "Ständig krank? Infektanfälligkeit & Immunsystem | Praxis IB Wien",
    metaDescription:
      "Wiederkehrende Infekte, lange Erholung, stille Entzündung: Lymphozytensubtypisierung, Mikrobiom und Mikronährstoffe verständlich erklärt – Praxis IB Wien.",
    keywords: [
      "Infektanfälligkeit Wien",
      "ständig krank Immunsystem",
      "Lymphozytensubtypisierung",
      "Immunsystem stärken Mikrobiom",
      "stille Entzündung",
    ],
    synonyms: [
      "jeder Infekt zieht sich wochenlang",
      "ständig erkältet",
      "wiederkehrende Herpes- oder Pilzinfektionen",
      "Immunsystem nach Antibiotika geschwächt",
    ],
    intro:
      "Ein Immunsystem, das häufig nachgibt, ist meist nicht „zu schwach“, sondern schlecht reguliert. Rund 70 Prozent der Immunzellen sitzen im darmassoziierten lymphatischen Gewebe – dort entscheidet sich ein großer Teil der Immunantwort.",
    sections: [
      {
        heading: "Regulation statt Stärkung",
        paragraphs: [
          "Die Vorstellung, das Immunsystem „hochzufahren“, greift zu kurz. Wichtiger ist das Gleichgewicht zwischen Abwehrbereitschaft und Toleranz. Regulatorische T-Zellen, die maßgeblich von mikrobiellen Metaboliten geprägt werden, spielen dabei eine zentrale Rolle.",
        ],
      },
      {
        heading: "Was eine Lymphozytensubtypisierung zeigen kann",
        paragraphs: [
          "Sie stellt dar, wie sich T-Helferzellen, zytotoxische T-Zellen, B-Zellen und NK-Zellen verteilen. Bei anhaltender Infektanfälligkeit, nach schweren Infekten oder bei Verdacht auf chronische Reaktivierungen liefert sie deutlich mehr Information als ein Differentialblutbild allein.",
        ],
      },
      {
        heading: "Ein Immunsystem lässt sich wieder stabilisieren",
        paragraphs: [
          "Das Immunsystem ist kein feststehender Zustand, sondern ein lernendes, regenerationsfähiges System. Wenn Barriere, Mikrobiota, Mikronährstoffe, Schlaf und Stressachse gemeinsam bearbeitet werden, verlängern sich die infektfreien Intervalle in der Regel deutlich – häufig innerhalb eines einzigen Jahresverlaufs.",
          "Viele Menschen beschreiben nach einigen Monaten kürzere und mildere Infekte, weniger Antibiotikabedarf und eine spürbar zurückgekehrte Belastbarkeit. Diese Entwicklung ist die Regel, nicht der Glücksfall.",
        ],
      },
    ],
    signs: [
      "mehr als vier bis sechs Infekte pro Jahr",
      "Infekte, die immer wieder nach unten wandern",
      "lange Erholungsphasen nach jedem Infekt",
      "wiederkehrende Reaktivierungen (z. B. Herpes labialis)",
      "geschwächte Abwehr nach mehreren Antibiotikatherapien",
    ],
    approach: [
      "Anamnese der Infektmuster und Vortherapien",
      "gezielte Immundiagnostik, wenn sie eine Konsequenz hat",
      "Aufbau der Schleimhautimmunität über die Mikrobiota",
      "Mikronährstoffe mit belegter Immunrelevanz (Vitamin D, Zink, Selen, Vitamin A)",
      "Berücksichtigung der Stressachse als Immunmodulator",
    ],
    faq: [
      {
        q: "Wie viele Infekte pro Jahr sind normal?",
        a: "Bei Erwachsenen gelten zwei bis vier Atemwegsinfekte jährlich als üblich. Deutlich mehr, sehr lange Verläufe oder häufige Komplikationen rechtfertigen eine genauere Abklärung.",
      },
      {
        q: "Helfen Probiotika?",
        a: "Stammspezifisch und gezielt eingesetzt können sie hilfreich sein. Ein beliebiges Präparat ohne Befund bringt dagegen meist wenig.",
      },
    ],
    relatedPosts: [
      "lymphozytensubtypisierung-immunsystem-verstehen",
      "mikrobielles-immunsystem-und-dessen-einfluss-auf-die-immunantwort",
      "effektive-mikroorganismen-em-wirkung-anwendung-mythen",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "stress-hormone-schlaf",
    title: "Dauerstress, Hormonbalance und schlechter Schlaf",
    shortTitle: "Stress, Hormone & Schlaf",
    metaTitle: "Dauerstress, Hormone & Schlaf: die HPA-Achse verstehen | Praxis IB Wien",
    metaDescription:
      "Wie Stressachse, Neurotransmitter und Mikrobiom zusammenhängen – und warum Schlafprobleme, innere Unruhe und Erschöpfung oft dieselbe Wurzel haben.",
    keywords: [
      "Dauerstress Körper",
      "HPA Achse Cortisol",
      "Schlafprobleme Ursachen",
      "Psychoneuroimmunologie Wien",
      "innere Unruhe komplementär",
    ],
    synonyms: [
      "nachts wach zwischen 2 und 4 Uhr",
      "innere Unruhe trotz Müdigkeit",
      "Erschöpfung nach langer Belastungsphase",
      "Reizbarkeit und Konzentrationsprobleme",
    ],
    intro:
      "Anhaltender Stress ist kein Gefühl, sondern ein biochemischer Zustand. Er verändert Cortisolrhythmus, Immunantwort, Verdauung und Schlafarchitektur – und wirkt dadurch direkt auf körperliche Beschwerden.",
    sections: [
      {
        heading: "Was die Psychoneuroimmunologie beschreibt",
        paragraphs: [
          "Die PNI untersucht, wie Psyche, Nervensystem, Hormonsystem und Immunsystem miteinander kommunizieren. Diese Systeme arbeiten nicht getrennt, sondern tauschen laufend Signale über Botenstoffe aus – über Zytokine, Hormone und Neurotransmitter.",
          "Mitochondrien sind dabei mehr als Energielieferanten: Sie geben Signale ab, die Immunzellen und Neurotransmitterbildung beeinflussen. Störungen in dieser Signalgebung wirken sich auf Entzündungsregulation, Zellstoffwechsel und Schmerzwahrnehmung aus.",
        ],
      },
      {
        heading: "Der Darm als Ort der Neurotransmitterbildung",
        paragraphs: [
          "Ein erheblicher Teil der Serotoninproduktion findet im Darm statt, und mikrobielle Stoffwechselprodukte beeinflussen GABA- und Dopaminwege. Eine unausgewogene Mikrobiota kann deshalb Schlaf, Antrieb und Stressresistenz spürbar mitprägen.",
        ],
      },
      {
        heading: "Regulation ist erlernbar",
        paragraphs: [
          "Hormonelle und vegetative Regelkreise sind darauf ausgelegt, sich zu erholen. Genau deshalb sind Verbesserungen hier oft eindrucksvoll: erholsamerer Schlaf, stabilere Energie über den Tag, ausgeglichenere Stimmung und ein Zyklus, der wieder Rhythmus findet – meist innerhalb weniger Monate.",
          "Auch bei Schilddrüsen- und Nebennierenthemen gilt: Selbst wenn eine Grunderkrankung bestehen bleibt, ist eine sehr gute Lebensqualität in aller Regel erreichbar. Der Körper braucht dafür vor allem verlässliche Bedingungen – und Zeit.",
        ],
      },
    ],
    signs: [
      "Einschlafen fällt leicht, Durchschlafen nicht",
      "morgens erschöpft, abends aufgedreht",
      "Verdauungsbeschwerden unter Belastung",
      "erhöhte Infektanfälligkeit in Stressphasen",
      "Muskelverspannung, Zähneknirschen, innere Unruhe",
    ],
    approach: [
      "Betrachtung von Tagesrhythmus, Belastung und Regeneration",
      "gezielte Diagnostik von Cortisolverlauf und relevanten Mikronährstoffen",
      "Magnesium-, B-Vitamin- und Aminosäurestatus als Basis der Neurotransmitterbildung",
      "adaptogene Phytotherapie nach individueller Konstitution",
      "Mikrobiomaufbau zur Unterstützung der Darm-Hirn-Achse",
    ],
    faq: [
      {
        q: "Ist das eine Psychotherapie?",
        a: "Nein. Ich arbeite an der körperlichen Seite der Stressregulation. Psychotherapeutische Begleitung ist eine eigene, oft sinnvolle Ergänzung.",
      },
      {
        q: "Kann ein Cortisoltagesprofil helfen?",
        a: "Wenn Beschwerden und Tagesverlauf nicht zusammenpassen, liefert es wertvolle Hinweise. Es ersetzt aber keine endokrinologische Abklärung bei Verdacht auf eine Erkrankung der Nebenniere.",
      },
    ],
    relatedPosts: [
      "die-nadel-im-heuhaufen-das-mikrobiom-und-sein-einfluss",
      "blutwerte-sinnvoll-labordiagnostik-leitfaden",
      "die-longevity-luege",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "urtikaria-nesselsucht",
    title: "Chronische Urtikaria: wenn die Haut täglich Alarm schlägt",
    shortTitle: "Urtikaria & Nesselsucht",
    metaTitle: "Chronische Urtikaria & Nesselsucht: Ursachen verstehen | Praxis IB Wien",
    metaDescription:
      "Chronisch spontane Urtikaria: Mastzellaktivierung, Histaminstoffwechsel, Mikrobiom und Infektfoki verstehen – komplementärmedizinische Begleitung in Wien.",
    keywords: [
      "chronische Urtikaria",
      "Nesselsucht Ursachen",
      "Quaddeln ohne Auslöser",
      "Mastzellaktivierung",
      "Urtikaria Wien Komplementärmedizin",
    ],
    synonyms: [
      "Nesselsucht",
      "Quaddeln am ganzen Körper",
      "Juckreiz ohne erkennbaren Auslöser",
      "chronisch spontane Urtikaria (csU)",
      "Angioödem / Schwellungen an Lippen und Lidern",
    ],
    intro:
      "Wenige Beschwerdebilder zermürben so gründlich wie eine chronische Urtikaria. Die Quaddeln kommen, wandern, verschwinden – und hinterlassen die Frage, warum niemand einen Auslöser findet. Genau das ist medizinisch typisch: Bei der chronisch spontanen Urtikaria existiert in der Mehrzahl der Fälle kein einzelnes Allergen. Nicht, weil nichts da wäre, sondern weil die Ursache nicht in einem Auslöser liegt, sondern in einer erniedrigten Reizschwelle der Mastzelle.",
    sections: [
      {
        heading: "Was in der Haut tatsächlich passiert",
        paragraphs: [
          "Die Quaddel entsteht durch Degranulation kutaner Mastzellen. Freigesetzt werden Histamin, Tryptase, Leukotriene und Prostaglandin D2; die Folge sind Vasodilatation, Plasmaaustritt in die Dermis und Aktivierung sensorischer C-Fasern – Rötung, Schwellung, Juckreiz. Das erklärt, warum Antihistaminika verlässlich lindern, aber nichts an der Ursache ändern: Sie besetzen den H1-Rezeptor, sie stabilisieren nicht die Zelle, die den Botenstoff freisetzt.",
          "Bei einem erheblichen Anteil der chronisch spontanen Urtikaria liegt eine autoimmune Komponente vor: IgG-Autoantikörper gegen den hochaffinen IgE-Rezeptor (FcεRI) oder gegen IgE selbst (Typ IIb), oder IgE-Autoantikörper gegen körpereigene Strukturen wie Thyreoperoxidase (Typ I). Deshalb ist eine Urtikaria, die seit Monaten besteht, keine Allergie im klassischen Sinn, sondern ein Zustand chronischer Immunaktivierung.",
        ],
      },
      {
        heading: "Warum die Suche nach dem einen Auslöser meist scheitert",
        paragraphs: [
          "Klinisch sinnvoller als die Frage „Was löst es aus?“ ist die Frage „Was senkt die Schwelle?“. Mastzellen reagieren nicht nur auf IgE, sondern auf Neuropeptide wie Substanz P (über den MRGPRX2-Rezeptor), auf Komplementfaktoren C3a und C5a, auf Zytokine aus stiller Entzündung, auf Stresshormone und auf mikrobielle Bestandteile. Steht die Zelle unter Dauerreiz, genügt anschließend ein banaler Impuls – Wärme, Druck, ein Glas Wein, ein Infekt – um die Kaskade auszulösen.",
          "Das erklärt auch die frustrierende Erfahrung vieler Betroffener: Das Ernährungstagebuch zeigt kein Muster, weil das Muster nicht im Auslöser liegt, sondern in der Summe der Vorbelastungen an diesem Tag.",
        ],
      },
      {
        heading: "Histaminstoffwechsel, Mikrobiom und Infektfoki",
        paragraphs: [
          "Histamin stammt nicht nur aus Mastzellen. Es wird mit der Nahrung aufgenommen, von bestimmten Darmbakterien gebildet (unter anderem histidindecarboxylase-positive Laktobazillen und Enterobakterien) und über Diaminoxidase (DAO) im Darmepithel sowie Histamin-N-Methyltransferase (HNMT) intrazellulär abgebaut. Eine gestörte Barriere und eine dysbiotische Flora erhöhen die Histaminlast von unten, während die Mastzelle von oben nachlegt.",
          "Ebenso relevant sind persistierende Entzündungsherde: Helicobacter pylori, chronische Sinusitiden, dentale Foki, Parasitosen oder – seltener beachtet – schwelende urogenitale Infektionen. Auch eine Autoimmunthyreoiditis (Hashimoto) findet sich bei Urtikaria überzufällig häufig. Diese Zusammenhänge sind gut belegt und trotzdem im Praxisalltag oft unbearbeitet.",
        ],
      },
      {
        heading: "Was das für die Begleitung bedeutet",
        paragraphs: [
          "Ich arbeite hier ausdrücklich ergänzend zur dermatologischen Behandlung. Antihistaminika oder eine Anti-IgE-Therapie sind dort, wo sie indiziert sind, sinnvoll und gehören nicht abgesetzt. Meine Aufgabe liegt daneben: die Reizschwelle wieder anzuheben, indem Barriere, Mikrobiota, Histaminabbau, Mikronährstoffstatus und Stressachse adressiert werden. Vitamin D, Zink, Kupfer und Vitamin B6 sind Kofaktoren des Histaminabbaus – Defizite lassen sich messen und korrigieren.",
        ],
      },
      {
        heading: "Remission ist bei Urtikaria häufig",
        paragraphs: [
          "Die chronische Urtikaria hat eine bemerkenswerte Eigenschaft: Sie neigt zur Remission. Ein erheblicher Teil der Verläufe kommt im Lauf der Zeit zum Stillstand – und dieser Prozess lässt sich unterstützen, indem Mastzellstabilität, Histaminabbau, Darmbarriere und Entzündungslast gezielt bearbeitet werden.",
          "Realistisch sind seltenere und mildere Schübe, weniger Bedarf an Antihistaminika und längere erscheinungsfreie Phasen; nicht selten verschwinden die Quaddeln über Monate vollständig. Selbst nach Jahren mit täglichen Schüben ist diese Wendung möglich – ich habe sie oft genug gesehen, um es zuversichtlich zu sagen.",
        ],
      },
    ],
    signs: [
      "Quaddeln, die innerhalb von 24 Stunden ohne Narbe abheilen und andernorts neu entstehen",
      "Juckreiz, der abends und nachts zunimmt",
      "Schwellungen an Lippen, Lidern oder Händen (Angioödem)",
      "Schübe nach Wärme, Druck, Alkohol, Stress oder Infekten",
      "Allergietests ohne wegweisendes Ergebnis",
      "Beschwerden bestehen länger als sechs Wochen",
    ],
    approach: [
      "ausführliche Anamnese zu Verlauf, Vorbefunden, Medikation und Schubmustern",
      "Labor: Entzündungs-, Schilddrüsen- und Autoimmunmarker, Ferritin, Vitamin D, Zink, Kupfer, Vitamin B6",
      "Abklärung von Infektfoki (u. a. Helicobacter pylori, urogenital, dental) und mikrobiologische Stuhldiagnostik",
      "Bewertung der Histaminlast: Ernährung, DAO-Kofaktoren, histaminbildende Flora",
      "gezielter Aufbau von Schleimhautbarriere und Mikrobiota statt pauschaler Dauerkarenz",
      "phytotherapeutische und mikronährstoffbasierte Mastzellstabilisierung, begleitend zur fachärztlichen Therapie",
    ],
    faq: [
      {
        q: "Ist eine chronische Urtikaria eine Allergie?",
        a: "In den meisten Fällen nicht. Die chronisch spontane Urtikaria ist überwiegend autoimmun oder autoallergisch bedingt. Deshalb bleiben Allergietests oft unauffällig – das ist kein Widerspruch zu Ihren Beschwerden, sondern der typische Befund.",
      },
      {
        q: "Soll ich histaminarm essen?",
        a: "Zeitlich begrenzt kann eine histaminreduzierte Ernährung die Gesamtlast senken und Ruhe in den Verlauf bringen. Als Dauerlösung ist sie ungünstig, weil sie die Vielfalt der Darmflora verringert und damit langfristig gegen das Ziel arbeitet. Der Aufbau der Toleranz ist das eigentliche Programm.",
      },
      {
        q: "Muss ich meine Antihistaminika absetzen?",
        a: "Nein. Über Ihre fachärztliche Medikation entscheidet die behandelnde Ärztin oder der behandelnde Arzt. Ich arbeite ergänzend an den Faktoren, die die Reizschwelle senken – nicht gegen die bestehende Therapie.",
      },
      {
        q: "Wie lange dauert es, bis sich etwas ändert?",
        a: "Realistisch sind mehrere Monate. Mastzellen, Schleimhaut und Mikrobiota verändern sich nicht in Wochen. Erste Hinweise auf eine Richtung zeigen sich meist an der Schubfrequenz, nicht an der einzelnen Quaddel.",
      },
    ],
    relatedPosts: [
      "neurodermitis-psoriasis-mikrobiom",
      "mikrobielles-immunsystem-und-dessen-einfluss-auf-die-immunantwort",
      "die-nadel-im-heuhaufen-das-mikrobiom-und-sein-einfluss",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "neurodermitis-psoriasis",
    title: "Neurodermitis und Psoriasis: zwei Wege, eine gemeinsame Achse",
    shortTitle: "Neurodermitis & Psoriasis",
    metaTitle: "Neurodermitis & Psoriasis: Mikrobiom, Barriere, Immunachse | Praxis IB Wien",
    metaDescription:
      "Atopische Dermatitis und Psoriasis verstehen: Hautbarriere, Th2- und Th17-Achse, Darm-Haut-Achse und Mikrobiom – komplementärmedizinische Begleitung in Wien.",
    keywords: [
      "Neurodermitis Mikrobiom",
      "Psoriasis Darm",
      "atopische Dermatitis Ursachen",
      "Schuppenflechte komplementärmedizinisch",
      "Darm-Haut-Achse Wien",
    ],
    synonyms: [
      "atopische Dermatitis",
      "Schuppenflechte",
      "Ekzem in den Beugen",
      "juckende, schuppende Hautstellen",
      "Psoriasis-Arthritis (Haut und Gelenke betroffen)",
    ],
    intro:
      "Neurodermitis und Psoriasis werden oft in einen Topf geworfen, weil beide chronisch, sichtbar und belastend sind. Immunologisch sind es zwei unterschiedliche Programme – und gerade deshalb lohnt es sich, genau hinzusehen. Wer über Jahre von Salbe zu Salbe weitergereicht wurde, hat meist nicht zu wenig Behandlung erhalten, sondern zu wenig Erklärung.",
    sections: [
      {
        heading: "Zwei Erkrankungen, zwei immunologische Handschriften",
        paragraphs: [
          "Die atopische Dermatitis ist primär eine Barriere-Erkrankung mit Th2-Dominanz. Häufig liegt eine Filaggrin-Variante vor: Das Protein, das die Hornschicht verkittet und Feuchthaltefaktoren liefert, fehlt oder funktioniert schlechter. Wasser geht verloren, Allergene und Mikroben dringen leichter ein, und die Zytokine IL-4, IL-13 und IL-31 treiben Entzündung und Juckreiz. Der Kratzreflex zerstört die Barriere weiter – ein sich selbst erhaltender Kreis.",
          "Die Psoriasis folgt der IL-23/Th17-Achse. Dendritische Zellen aktivieren Th17-Lymphozyten, IL-17 und IL-22 beschleunigen die Keratinozytenteilung dramatisch: Statt in rund vier Wochen erneuert sich die Epidermis in wenigen Tagen. Daraus entstehen die typischen silbrigen Schuppen. Psoriasis ist damit keine reine Hautsache, sondern eine systemische Entzündung – mit bekannter Assoziation zu Gelenkbeteiligung, metabolischem Syndrom und Herz-Kreislauf-Risiko.",
        ],
      },
      {
        heading: "Die Darm-Haut-Achse ist keine Metapher",
        paragraphs: [
          "Beide Erkrankungen zeigen in Studien Auffälligkeiten der Darmflora: verringerte Diversität, weniger Butyratbildner wie Faecalibacterium prausnitzii, geringere Besiedlung mit Akkermansia muciniphila. Butyrat ernährt nicht nur die Kolonozyten, es hemmt Histondeacetylasen und fördert regulatorische T-Zellen – also genau jene Zellpopulation, die überschießende Th2- und Th17-Antworten bremst. Fehlt diese Bremse, entsteht ein systemisch entzündungsfreundliches Milieu, das sich an der Haut manifestiert.",
          "Bei Psoriasis kommt hinzu, dass Lipopolysaccharide (LPS) gramnegativer Bakterien bei durchlässiger Barriere in den Kreislauf gelangen und über TLR4 die Entzündungskaskade zusätzlich befeuern. Der Zusammenhang zwischen Darmpermeabilität und psoriatischer Aktivität ist mehrfach beschrieben.",
        ],
      },
      {
        heading: "Das Hautmikrobiom selbst",
        paragraphs: [
          "Bei der Neurodermitis verschiebt sich die kutane Flora im Schub zugunsten von Staphylococcus aureus, der über Superantigene und Proteasen die Barriere weiter schädigt und den Juckreiz verstärkt. Kommensale wie Staphylococcus epidermidis und Cutibacterium-Arten, die antimikrobielle Peptide und Fettsäuren produzieren, gehen zurück. Eine Behandlung, die nur desinfiziert, ohne die Kommensalen wieder zu ermöglichen, greift daher zu kurz.",
        ],
      },
      {
        heading: "Wie ich damit arbeite",
        paragraphs: [
          "Dermatologische Therapie – topische Steroide, Calcineurininhibitoren, Biologika – bleibt Sache der Fachärztin oder des Facharztes und ist bei mittelschweren bis schweren Verläufen unverzichtbar. Meine Arbeit setzt an der Grundlast an: Barriere und Mikrobiota im Darm, Entzündungsmodulation über Omega-3-Fettsäuren und Polyphenole, Vitamin-D-Status, Zink, Selen, Nachtkerzen- und Borretschsamenöl bei atopischer Haut, Bewertung von Stress- und Schlafachse, und – bei Psoriasis – ein realistischer Blick auf Insulinresistenz, Alkohol und Nikotin als Treiber.",
          "Was ich nicht mache: Heilung versprechen. Was ich mache: Ihnen erklären, welche Achse bei Ihnen läuft, was messbar ist und welche Stellschrauben tatsächlich Ihre sind.",
        ],
      },
      {
        heading: "Lange ruhige Phasen sind ein realistisches Ziel",
        paragraphs: [
          "Neurodermitis und Psoriasis verlaufen in Schüben – und genau darin liegt die gute Nachricht: Schubfrequenz, Schubdauer und Schwere sind beeinflussbar. Wenn Barriere, Mikrobiota, Entzündungslast und Stressachse gemeinsam bearbeitet werden, verlängern sich die ruhigen Phasen in der Mehrzahl der Verläufe deutlich.",
          "Viele Menschen erreichen eine weitgehend erscheinungsfreie Haut über längere Zeiträume, brauchen weniger topische Kortikosteroide und schlafen wieder durch, weil der Juckreiz nachlässt. Das ist kein Heilungsversprechen, aber ein sehr erreichbares Ziel.",
        ],
      },
    ],
    signs: [
      "juckende, gerötete Ekzeme in Ellenbeugen, Kniekehlen, Hals oder Händen",
      "scharf begrenzte, silbrig schuppende Plaques an Ellenbogen, Knien, Kopfhaut oder Nabel",
      "Nagelveränderungen (Tüpfel, Ölflecken) oder begleitende Gelenkbeschwerden",
      "deutliche Schübe nach Infekten, Stress oder Alkohol",
      "trockene, empfindliche Haut mit ständigem Spannungsgefühl",
      "begleitend Heuschnupfen, Asthma oder Nahrungsmittelreaktionen (atopischer Formenkreis)",
    ],
    approach: [
      "Anamnese von Verlauf, Schubauslösern, Vorbehandlungen und familiärer Belastung",
      "Labor: Entzündungsmarker, Vitamin D, Zink, Selen, Omega-3-Index, Eisenstatus, ggf. Schilddrüse und Insulinresistenz",
      "mikrobiologische Stuhldiagnostik mit Barriere- und Entzündungsmarkern (u. a. Zonulin, sekretorisches IgA, Alpha-1-Antitrypsin, Calprotectin)",
      "gezielter Aufbau butyratbildender Flora statt wahlloser Probiotikagabe",
      "entzündungsmodulierende Mikronährstoff- und Phytotherapie, abgestimmt auf die vorliegende Immunachse",
      "enge Abstimmung mit der laufenden dermatologischen Behandlung",
    ],
    faq: [
      {
        q: "Kann man Neurodermitis oder Psoriasis über den Darm heilen?",
        a: "Nein, und jede gegenteilige Aussage sollte Sie misstrauisch machen. Beide Erkrankungen haben eine genetische Grundlage. Was sich beeinflussen lässt, ist die Entzündungslast und damit Häufigkeit und Schwere der Schübe – und das ist für die Lebensqualität oft entscheidend.",
      },
      {
        q: "Brauche ich eine Stuhlanalyse?",
        a: "Sie ist sinnvoll, wenn zusätzlich Verdauungsbeschwerden bestehen, Antibiotika in der Vorgeschichte eine Rolle spielten oder der Verlauf trotz konsequenter Hautpflege therapieresistent bleibt. Ich entscheide das nach der Anamnese, nicht nach Schema.",
      },
      {
        q: "Muss ich Lebensmittel weglassen?",
        a: "Pauschale Eliminationsdiäten sind bei Hauterkrankungen selten hilfreich und bei Kindern potenziell schädlich. Sinnvoller ist eine Ernährung, die entzündungsarm ist und die Mikrobiota nährt – also mehr Vielfalt, nicht weniger.",
      },
      {
        q: "Ich habe Psoriasis und Gelenkschmerzen. Gehört das zusammen?",
        a: "Möglicherweise ja. Bis zu einem Drittel der Betroffenen entwickelt eine Psoriasis-Arthritis. Das gehört rheumatologisch abgeklärt – und je früher, desto besser, weil frühe Behandlung Gelenkschäden verhindern kann.",
      },
    ],
    relatedPosts: [
      "neurodermitis-psoriasis-mikrobiom",
      "akkermansia-muciniphila-faecalibacterium-prausnitzii-darmflora-schleimhaut",
      "mikrobielles-immunsystem-und-dessen-einfluss-auf-die-immunantwort",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "rheumatische-beschwerden-arthritis-arthrose-fibromyalgie",
    title: "Rheumatische Beschwerden: reaktive Arthritis, Arthrose und Fibromyalgie",
    shortTitle: "Rheuma, Arthrose & Fibromyalgie",
    metaTitle: "Reaktive Arthritis, Arthrose & Fibromyalgie: Ursachen | Praxis IB Wien",
    metaDescription:
      "Gelenk- und Muskelschmerzen verstehen: reaktive Arthritis nach Infekt, Arthrose als Entzündungsgeschehen, Fibromyalgie und Mikrobiom – Begleitung in Wien.",
    keywords: [
      "reaktive Arthritis nach Infekt",
      "Arthrose Entzündung Ernährung",
      "Fibromyalgie Ursachen",
      "Gelenkschmerzen Mikrobiom",
      "Rheuma komplementärmedizinisch Wien",
    ],
    synonyms: [
      "Gelenkschmerzen ohne Rheumafaktor",
      "Arthritis nach Darm- oder Harnwegsinfekt",
      "Gelenkverschleiß / Abnützung",
      "Ganzkörperschmerz",
      "Muskelschmerzen und Druckpunkte",
      "Morgensteifigkeit",
    ],
    intro:
      "Kaum jemand kommt mit Gelenk- oder Ganzkörperschmerzen als erste Station in meine Praxis. Meist liegen Jahre dahinter, mehrere Fachrichtungen, viele Befunde – und der Satz, man müsse damit eben leben lernen. Dieser Satz stimmt selten vollständig. Auch wenn eine Erkrankung nicht heilbar ist, ist die Entzündungslast, unter der sie verläuft, in erstaunlichem Ausmaß beeinflussbar.",
    sections: [
      {
        heading: "Reaktive Arthritis: der Infekt ist weg, das Gelenk nicht",
        paragraphs: [
          "Eine reaktive Arthritis entsteht typischerweise ein bis vier Wochen nach einer gastrointestinalen oder urogenitalen Infektion – klassisch nach Chlamydia trachomatis, Yersinia, Salmonella, Campylobacter oder Shigella. Betroffen sind meist wenige große Gelenke der unteren Extremität, oft asymmetrisch, häufig begleitet von Enthesitis (Sehnenansatzentzündung) oder Daktylitis. Bei HLA-B27-positiven Personen ist das Risiko deutlich erhöht.",
          "Pathophysiologisch diskutiert werden persistierende mikrobielle Antigene im Gelenk und molekulare Mimikry: Bakterielle Strukturen ähneln körpereigenen so weit, dass die Immunantwort mit angreift. Das erklärt, warum ein negativer Erregernachweis im Gelenkpunktat die Diagnose nicht ausschließt – und warum die Frage nach dem vorangegangenen Infekt, oft Monate zurück, klinisch so wertvoll ist.",
        ],
      },
      {
        heading: "Arthrose ist mehr als Abnützung",
        paragraphs: [
          "Das Bild vom mechanisch verschlissenen Gelenk ist überholt. Die moderne Sicht beschreibt Arthrose als niedriggradig entzündliches Geschehen des gesamten Gelenkorgans: Chondrozyten setzen unter Belastung IL-1β, TNF-α und Matrix-Metalloproteinasen frei, die Synovialis reagiert mit einer milden Synovitis, der subchondrale Knochen remodelliert. Der Knorpelabbau ist damit nicht nur Folge von Druck, sondern von biochemischer Aktivität.",
          "Genau hier wird die systemische Entzündungslast relevant. Viszerales Fettgewebe produziert Adipokine wie Leptin, die Chondrozyten katabol stimulieren; eine Insulinresistenz verstärkt das. Deshalb betrifft Arthrose auch nicht belastete Gelenke wie die Fingergelenke – ein rein mechanisches Modell könnte das nicht erklären.",
        ],
      },
      {
        heading: "Fibromyalgie: zentrale Sensibilisierung mit peripherem Kontext",
        paragraphs: [
          "Die Fibromyalgie ist kein Ausschluss dessen, was man nicht gefunden hat, sondern ein eigenständiges Störungsbild der Schmerzverarbeitung. Kennzeichnend sind zentrale Sensibilisierung mit erhöhter Substanz-P-Konzentration im Liquor, eine abgeschwächte absteigende Schmerzhemmung und eine veränderte Aktivität der Hypothalamus-Hypophysen-Nebennierenrinden-Achse. Bildgebende Studien zeigen reale Unterschiede der zentralnervösen Schmerzverarbeitung. Die Schmerzen sind nicht eingebildet – sie sind anders verarbeitet.",
          "Daneben finden sich regelmäßig relevante Begleitfaktoren: subklinische Schilddrüsenfunktionsstörungen und Hashimoto-Thyreoiditis, Eisen- und Vitamin-D-Mangel, Magnesium- und B-Vitamin-Defizite, mitochondriale Erschöpfung der Muskulatur, Schlafarchitekturstörungen mit fehlendem Tiefschlaf – und eine in mehreren Arbeiten beschriebene Dysbiose mit verringerter Diversität und veränderter Produktion kurzkettiger Fettsäuren und Neurotransmittervorstufen.",
        ],
      },
      {
        heading: "Die gemeinsame Klammer: Darm, Barriere und Immunsystem",
        paragraphs: [
          "Rund 70 Prozent der Immunzellen liegen im darmassoziierten lymphatischen Gewebe. Eine durchlässige Schleimhaut lässt bakterielle Bestandteile – insbesondere LPS – in den Kreislauf gelangen, wo sie über TLR4 eine dauerhafte, unterschwellige Immunaktivierung unterhalten. Diese „silent inflammation“ senkt die Schmerzschwelle, hält Synovialgewebe in Alarmbereitschaft und belastet die Mitochondrien.",
          "Bei hartnäckigen, therapieresistenten Verläufen mit Infektvorgeschichte setze ich zusätzlich moderne mikrobiologische Diagnostik ein – qPCR und Next-Generation-Sequencing über MicroGenDX –, weil kulturbasierte Verfahren biofilmassoziierte und schwer anzüchtbare Erreger regelmäßig übersehen. Nicht bei jedem Menschen ist das nötig. Aber bei jenen, bei denen nie etwas gefunden wurde, ist es manchmal genau der fehlende Befund.",
        ],
      },
      {
        heading: "Was Sie von mir erwarten können – und was nicht",
        paragraphs: [
          "Ich ersetze keine Rheumatologie. Entzündlich-rheumatische Erkrankungen gehören fachärztlich diagnostiziert und, wo indiziert, immunmodulatorisch behandelt; eine begonnene Basistherapie setzt man nicht ab. Was ich beitrage, ist die Arbeit an der Grundlast: Entzündungsmodulation, Mikronährstoff- und Mitochondrienstatus, Mikrobiota und Barriere, Infektfoki, Schlaf und Stressachse.",
          "Und ich nehme mir die Zeit, Ihre Befunde der letzten Jahre gemeinsam mit Ihnen zu lesen. Sehr oft steht dort mehr, als je erklärt wurde.",
        ],
      },
      {
        heading: "Was sich verändern lässt – und das ist viel",
        paragraphs: [
          "Auch wenn eine rheumatische Erkrankung bestehen bleibt: Die Entzündungslast, unter der sie verläuft, ist in erstaunlichem Ausmaß beeinflussbar. Weniger Morgensteifigkeit, geringere Schmerzintensität, bessere Beweglichkeit, mehr Belastbarkeit und erholsamerer Schlaf sind in der Mehrzahl der Verläufe erreichbar.",
          "Bei reaktiven Arthritiden ist ein vollständiges Ausheilen häufig, bei Fibromyalgie beschreiben viele Betroffene nach einigen Monaten Phasen, in denen der Schmerz in den Hintergrund tritt. Ein Leben, das wieder von Ihnen geplant wird und nicht von den Gelenken – das ist der Maßstab, und er ist realistisch.",
        ],
      },
    ],
    signs: [
      "Gelenkschwellung oder -schmerz, der Wochen nach einem Darm- oder Harnwegsinfekt begann",
      "Morgensteifigkeit, die länger als 30 Minuten anhält",
      "Schmerzen an Sehnenansätzen, Ferse oder einzelnen geschwollenen Fingern bzw. Zehen",
      "belastungsabhängiger Gelenkschmerz mit Anlaufschmerz nach Ruhe",
      "ausgedehnter Muskel- und Weichteilschmerz über mehr als drei Monate",
      "nicht erholsamer Schlaf, Konzentrationsstörungen und Reizempfindlichkeit begleitend",
    ],
    approach: [
      "strukturierte Durchsicht aller Vorbefunde – rheumatologisch, orthopädisch, labormedizinisch",
      "Labor: Entzündungsmarker, Eisen- und Ferritinstatus, Vitamin D, B12, Folsäure, Magnesium, Schilddrüse inkl. Antikörper, Homocystein",
      "Abklärung einer Infektvorgeschichte und – bei hartnäckigen Verläufen – qPCR-/NGS-Diagnostik über MicroGenDX",
      "mikrobiologische Stuhldiagnostik mit Barriere- und Entzündungsmarkern",
      "entzündungsmodulierende Ernährungs-, Mikronährstoff- und Phytotherapiestrategie (u. a. Omega-3, Curcumin-Zubereitungen in Arzneimittelqualität, Weihrauch, Polyphenole)",
      "Unterstützung der mitochondrialen Energiebereitstellung und der Schlaf- sowie Stressachse",
      "enge Abstimmung mit Rheumatologie, Orthopädie und Hausarztpraxis",
    ],
    faq: [
      {
        q: "Meine Rheumawerte sind normal – warum habe ich trotzdem Schmerzen?",
        a: "Normale Rheumafaktoren, CCP-Antikörper oder ein unauffälliges CRP schließen weder eine seronegative Arthritis noch eine reaktive Arthritis, eine Arthrose oder eine Fibromyalgie aus. Diese Marker bilden bestimmte Krankheitsmechanismen ab, nicht das Vorhandensein von Schmerz.",
      },
      {
        q: "Kann eine Ernährungsumstellung bei Arthrose wirklich etwas bewirken?",
        a: "Knorpel wächst nicht nach. Die entzündliche Aktivität rund um das Gelenk lässt sich aber beeinflussen, und genau sie bestimmt einen erheblichen Teil des Schmerzes und der Progression. Realistisch ist eine Verbesserung von Beschwerden und Funktion, keine Umkehr der Struktur.",
      },
      {
        q: "Ist Fibromyalgie psychosomatisch?",
        a: "Sie ist ein neurobiologisch fassbares Störungsbild der Schmerzverarbeitung. Psychische Belastung kann Verlauf und Schwelle beeinflussen – wie bei nahezu jeder chronischen Erkrankung –, sie ist aber nicht die Ursache. Diese Unterscheidung ist mir wichtig, weil viele Betroffene sie nie gehört haben.",
      },
      {
        q: "Muss ich meine rheumatologische Therapie ändern?",
        a: "Nein. Ich arbeite ergänzend und stimme mich mit Ihrer Fachärztin oder Ihrem Facharzt ab. Änderungen an Basistherapien oder Biologika treffe nicht ich.",
      },
      {
        q: "Wie lange dauert eine solche Begleitung?",
        a: "Nach der ersten Sitzung mit Anamnese und Befundbesprechung folgt meist eine Diagnostikphase, danach arbeiten wir in Etappen. Belastbare Veränderungen zeigen sich erfahrungsgemäß über drei bis sechs Monate, nicht über Wochen.",
      },
    ],
    relatedPosts: [
      "fibromyalgie-schilddruese-hormone-biofilm-mikrobiom",
      "die-rolle-von-biofilmen-in-der-symbiose-zwischen-nuetzlichen-und-pathogenen-mikroben",
      "eisenstoffwechsel-ferritin-transferrin-hepcidin",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "ced-morbus-crohn-colitis-ulcerosa",
    title: "Chronisch entzündliche Darmerkrankungen: Morbus Crohn und Colitis ulcerosa begleiten",
    shortTitle: "Morbus Crohn & Colitis ulcerosa",
    metaTitle: "Morbus Crohn & Colitis ulcerosa: komplementäre Begleitung | Praxis IB Wien",
    metaDescription:
      "Chronisch entzündliche Darmerkrankungen verstehen: Barriere, Mikrobiota, Mikronährstoffe und Entzündungsmodulation – ergänzend zur gastroenterologischen Therapie in Wien.",
    keywords: [
      "Morbus Crohn Wien",
      "Colitis ulcerosa komplementärmedizinisch",
      "CED Mikrobiom",
      "Remission unterstützen Darm",
      "chronisch entzündliche Darmerkrankung Ernährung",
    ],
    synonyms: [
      "CED",
      "Morbus Crohn",
      "Colitis ulcerosa",
      "Schub trotz Therapie",
      "blutige Durchfälle",
      "Calprotectin erhöht",
    ],
    intro:
      "Morbus Crohn und Colitis ulcerosa sind gastroenterologisch geführte Erkrankungen – und das sollen sie bleiben. Was ich beitrage, beginnt dort, wo die Immunsuppression greift, die Lebensqualität aber nicht zurückkehrt: bei Barriere, Mikrobiota, Nährstoffversorgung und der Entzündungsgrundlast, die zwischen den Schüben weiterläuft.",
    sections: [
      {
        heading: "Was eine CED auf zellulärer Ebene ausmacht",
        paragraphs: [
          "Beiden Erkrankungen liegt eine fehlgeleitete Immunantwort der Darmschleimhaut gegenüber der eigenen Mikrobiota zugrunde – bei genetischer Prädisposition (etwa NOD2-Varianten beim Morbus Crohn) und unter Umweltfaktoren. Der Morbus Crohn zeigt eine transmurale, segmentale Entzündung mit Th1-/Th17-Prägung und kann den gesamten Verdauungstrakt betreffen; die Colitis ulcerosa bleibt auf Mukosa und Submukosa des Kolons beschränkt und breitet sich kontinuierlich vom Rektum nach proximal aus.",
          "Gemeinsam ist beiden eine gestörte Barriere: veränderte Tight-Junction-Proteine, eine dünnere Mukusschicht, verminderte antimikrobielle Peptide der Paneth-Zellen. Bakterielle Bestandteile erreichen dadurch Immunzellen, die sie in einer intakten Schleimhaut nie sehen würden – die Entzündung unterhält sich selbst.",
        ],
      },
      {
        heading: "Die Mikrobiota ist bei CED nicht Nebenschauplatz",
        paragraphs: [
          "Charakteristisch ist eine reduzierte Diversität mit Verlust butyratbildender Bakterien, allen voran Faecalibacterium prausnitzii, dessen Rückgang in Studien mit einer höheren Rückfallrate nach Remission assoziiert ist. Gleichzeitig nehmen Proteobakterien zu, beim Morbus Crohn insbesondere adhärent-invasive Escherichia coli.",
          "Butyrat ist der wichtigste Energieträger der Kolonozyten und wirkt zugleich immunregulatorisch, indem es regulatorische T-Zellen fördert. Fehlt es, verliert die Schleimhaut gleichzeitig Energie und Bremse. Das erklärt, warum Mikrobiota-orientierte Arbeit bei CED kein Wellnessthema ist, sondern an einem realen Mechanismus ansetzt.",
        ],
      },
      {
        heading: "Mangelzustände, die den Verlauf mitbestimmen",
        paragraphs: [
          "Eisenmangel durch chronischen Blutverlust und entzündungsbedingt erhöhtes Hepcidin, Vitamin-B12-Mangel bei Befall oder Resektion des terminalen Ileums, Vitamin-D-Defizite mit immunologischer Relevanz, Zink-, Selen- und Folsäuremängel – teils krankheitsbedingt, teils medikationsassoziiert – sind bei CED die Regel und nicht die Ausnahme.",
          "Diese Defizite sind messbar und korrigierbar. Sie erklären häufig genau jene Erschöpfung, Infektanfälligkeit und Wundheilungsstörung, die auch in guter Remission bestehen bleibt und für die es im Befundbogen keine Zeile gibt.",
        ],
      },
      {
        heading: "Ergänzend – ausdrücklich nicht anstelle",
        paragraphs: [
          "Mesalazin, Kortikosteroide, Immunmodulatoren und Biologika werden nicht von mir angepasst und schon gar nicht abgesetzt. Mein Feld ist die Begleitung zwischen den Kontrollen: Ernährungsstrategie in und außerhalb des Schubs, Mikronährstoffkorrektur nach Laborbefund, verträgliche entzündungsmodulierende Phytotherapeutika in Arzneimittelqualität, Aufbau der Barriere in stabilen Phasen und die Stressachse, deren Einfluss auf die Schubfrequenz gut belegt ist.",
        ],
      },
      {
        heading: "Remission ist das Ziel – und oft erreichbar",
        paragraphs: [
          "Chronisch entzündliche Darmerkrankungen sind heute so gut behandelbar wie nie zuvor. Mit moderner gastroenterologischer Therapie erreichen viele Menschen eine anhaltende Remission; die komplementäre Arbeit an Versorgung, Barriere und Grundlast kann diese Phasen stabilisieren und verlängern.",
          "Was ich immer wieder sehe: Menschen, die in Remission trotzdem erschöpft waren, gewinnen ihre Energie zurück, sobald Eisen, B12, Vitamin D und die Mikrobiota wieder stimmen. Ein aktives, planbares Leben mit CED ist kein Trostpflaster, sondern für die meisten Betroffenen ein realistisches Ziel.",
        ],
      },
    ],
    signs: [
      "wiederkehrende Durchfälle, teils mit Blut oder Schleim",
      "krampfartige Bauchschmerzen, häufig im rechten Unterbauch",
      "ungewollter Gewichtsverlust und anhaltende Erschöpfung",
      "erhöhtes Calprotectin im Stuhl trotz subjektiv ruhiger Phase",
      "Begleitsymptome an Gelenken, Haut oder Augen",
      "Eisen- oder B12-Mangel, der immer wiederkehrt",
    ],
    approach: [
      "Durchsicht aller gastroenterologischen Befunde inklusive Endoskopie, Histologie und Calprotectinverlauf",
      "Labor: Eisenstatus mit Ferritin und Transferrinsättigung, B12, Folsäure, Vitamin D, Zink, Selen, Entzündungsmarker",
      "mikrobiologische Stuhldiagnostik mit Barriere-, Verdauungs- und Entzündungsparametern",
      "phasengerechte Ernährungsstrategie – entlastend im Schub, vielfaltsfördernd in Remission",
      "gezielte Mikronährstofftherapie und verträgliche Phytotherapeutika in Arzneimittelqualität",
      "Arbeit an Schlaf, Stressachse und Regeneration als anerkannten Einflussgrößen der Schubfrequenz",
      "laufende Abstimmung mit der behandelnden Gastroenterologie",
    ],
    faq: [
      {
        q: "Kann ich meine Medikamente durch Naturheilkunde ersetzen?",
        a: "Nein, und ich würde Ihnen davon dringend abraten. Eine CED ohne wirksame Entzündungskontrolle riskiert Komplikationen, die sich später nicht mehr rückgängig machen lassen. Komplementärmedizin arbeitet hier ergänzend – an Versorgung, Barriere und Grundlast.",
      },
      {
        q: "Gibt es eine Ernährung, die für alle passt?",
        a: "Nein. Im akuten Schub ist Entlastung sinnvoll, in Remission genau das Gegenteil: Vielfalt und Ballaststoffe, so viel wie individuell verträglich. Dauerhafte Restriktion verarmt die Mikrobiota und verschlechtert langfristig die Ausgangslage.",
      },
      {
        q: "Sind Probiotika bei CED sinnvoll?",
        a: "Differenziert. Bei Colitis ulcerosa gibt es für einzelne Stämme und Multispezies-Präparate eine solide Datenlage zur Remissionserhaltung, beim Morbus Crohn ist sie deutlich schwächer. Pauschale Empfehlungen sind hier fehl am Platz – die Auswahl richtet sich nach Diagnose, Befund und Phase.",
      },
    ],
    relatedPosts: [
      "akkermansia-muciniphila-faecalibacterium-prausnitzii-darmflora-schleimhaut",
      "die-nadel-im-heuhaufen-das-mikrobiom-und-sein-einfluss",
      "eisenstoffwechsel-ferritin-transferrin-hepcidin",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "divertikulitis",
    title: "Divertikel und Divertikulitis: Rückfälle verstehen und vorbeugen",
    shortTitle: "Divertikulitis",
    metaTitle: "Divertikulitis: Rückfälle vorbeugen, Darm stabilisieren | Praxis IB Wien",
    metaDescription:
      "Divertikulose und wiederkehrende Divertikulitis: Mikrobiota, Ballaststoffe, Entzündungsmodulation und Aufbau nach Antibiotikatherapie – Begleitung in Wien.",
    keywords: [
      "Divertikulitis Rückfall vermeiden",
      "Divertikulose Ernährung",
      "Divertikulitis Mikrobiom",
      "Darmaufbau nach Antibiotika",
      "Divertikulitis Wien",
    ],
    synonyms: [
      "Divertikel im Darm",
      "wiederkehrende Divertikulitis",
      "Schmerzen linker Unterbauch",
      "Darmentzündung nach Divertikeln",
      "SUDD – symptomatische unkomplizierte Divertikelkrankheit",
    ],
    intro:
      "Divertikel hat ein erheblicher Teil der Bevölkerung ab dem sechsten Lebensjahrzehnt – die meisten davon lebenslang beschwerdefrei. Interessant wird es dort, wo es nicht ruhig bleibt: bei wiederkehrenden Entzündungen und bei jenen anhaltenden Beschwerden, die auch zwischen den Schüben nicht verschwinden.",
    sections: [
      {
        heading: "Wie Divertikel entstehen – und warum sie sich entzünden",
        paragraphs: [
          "Divertikel sind Ausstülpungen der Schleimhaut durch muskelschwache Stellen der Darmwand, meist im Sigma, wo der Innendruck am höchsten ist. Begünstigt werden sie durch ballaststoffarme Ernährung, veränderte Kollagenstruktur mit zunehmendem Alter und eine gestörte Motilität.",
          "Die klassische Vorstellung, ein eingeklemmtes Körnchen löse die Entzündung aus, gilt heute als überholt. Aktuelle Modelle beschreiben stattdessen eine chronische niedriggradige Entzündung der Divertikelumgebung, eine lokale Dysbiose und eine veränderte enterische Nervenfunktion. Das erklärt auch, warum Nüsse und Körner nicht mehr verboten werden – Kohortendaten zeigen im Gegenteil eher ein geringeres Risiko.",
        ],
      },
      {
        heading: "Warum es nach dem Antibiotikum oft nicht besser wird",
        paragraphs: [
          "Die akute Divertikulitis wird leitliniengerecht behandelt, komplizierte Verläufe gehören in ärztliche und teils chirurgische Hand. Was danach häufig bleibt, ist ein zweifach getroffener Darm: die Entzündung selbst und die antibiotische Therapie haben die Mikrobiota reduziert, insbesondere butyratbildende Gattungen.",
          "Ohne Butyrat fehlt der Schleimhaut Energie und immunologische Regulation. Genau in dieser Lücke entstehen die typischen Restbeschwerden – Druck, Blähung, unregelmäßiger Stuhl – und in ihr liegt auch ein Teil des Rückfallrisikos. Der Wiederaufbau nach dem Schub ist deshalb kein optionales Extra.",
        ],
      },
      {
        heading: "Ballaststoffe: die richtige Menge zur richtigen Zeit",
        paragraphs: [
          "Im akuten Schub ist Entlastung angezeigt. Dauerhaft ist jedoch das Gegenteil protektiv: eine ballaststoffreiche Ernährung senkt den intraluminalen Druck, verkürzt die Transitzeit und ernährt genau jene Bakterien, deren Metabolite die Schleimhaut stabilisieren. Der Übergang zwischen beiden Phasen muss langsam erfolgen, sonst wird die richtige Maßnahme zur falschen Zeit gesetzt und als unverträglich abgespeichert.",
        ],
      },
      {
        heading: "Die Aussichten sind gut",
        paragraphs: [
          "Die allermeisten Menschen mit Divertikeln bleiben beschwerdefrei – und auch nach mehreren Schüben lässt sich das Rückfallrisiko wirksam senken. Ballaststoffvielfalt, stabile Mikrobiota, Bewegung und ein bewusster Umgang mit NSAR sind Faktoren mit belegter Schutzwirkung, und alle liegen in Ihrer Hand.",
          "Nach einem strukturierten Wiederaufbau berichten viele Betroffene, dass auch die zähen Restbeschwerden zwischen den Schüben verschwinden. Ein ruhiger, verlässlicher Darm ist hier in den meisten Fällen ein erreichbares Ziel.",
        ],
      },
    ],
    signs: [
      "wiederkehrende Schmerzen im linken Unterbauch",
      "Druck- und Völlegefühl, das nach Stuhlgang kurz nachlässt",
      "wechselnder Stuhlgang mit Neigung zur Verstopfung",
      "Beschwerden, die auch zwischen den Entzündungsschüben bestehen bleiben",
      "mehr als eine antibiotisch behandelte Divertikulitis in den letzten Jahren",
    ],
    approach: [
      "Sichtung von Koloskopie-, CT- und Laborbefunden sowie der bisherigen Schubverläufe",
      "mikrobiologische Stuhldiagnostik mit Butyratbildnern, Barriere- und Entzündungsmarkern",
      "strukturierter Wiederaufbau nach Antibiotikatherapie statt Zufallsprodukt aus der Drogerie",
      "schrittweise, verträglichkeitsgeführte Steigerung der Ballaststoffvielfalt",
      "entzündungsmodulierende Phytotherapie und Mikronährstoffe nach Befund",
      "klare Grenzziehung: akute oder komplizierte Verläufe gehören sofort ärztlich abgeklärt",
    ],
    faq: [
      {
        q: "Darf ich wirklich Nüsse und Körner essen?",
        a: "Nach heutiger Datenlage ja. Große prospektive Kohorten fanden kein erhöhtes, teils sogar ein vermindertes Risiko. Das alte Verbot beruhte auf einer plausibel klingenden Annahme, die sich nicht bestätigt hat.",
      },
      {
        q: "Wann muss ich sofort ärztlich abklären lassen?",
        a: "Bei starken oder rasch zunehmenden Schmerzen, Fieber, Erbrechen, hartem Bauch oder Blut im Stuhl. Das sind Warnzeichen einer komplizierten Divertikulitis und kein Feld für komplementärmedizinische Begleitung.",
      },
      {
        q: "Kann ich weitere Schübe verhindern?",
        a: "Eine Garantie gibt es nicht. Ballaststoffreiche Ernährung, stabile Mikrobiota, Bewegung, Gewichtsregulation und der bewusste Umgang mit NSAR sind aber jene Faktoren, für die eine risikosenkende Wirkung beschrieben ist – und alle davon sind beeinflussbar.",
      },
    ],
    relatedPosts: [
      "akkermansia-muciniphila-faecalibacterium-prausnitzii-darmflora-schleimhaut",
      "darmsanierung-teure-praeparate-heilversprechen",
      "die-nadel-im-heuhaufen-das-mikrobiom-und-sein-einfluss",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "prostatabeschwerden-cpps",
    title: "Prostatabeschwerden und chronisches Beckenschmerzsyndrom beim Mann",
    shortTitle: "Prostatabeschwerden & CPPS",
    metaTitle: "Prostatitis & chronisches Beckenschmerzsyndrom (CPPS) | Praxis IB Wien",
    metaDescription:
      "Chronische Prostatitis und CPPS: Biofilme, qPCR- und NGS-Diagnostik, Mikrobiom und Beckenboden – komplementärmedizinische Begleitung für Männer in Wien.",
    keywords: [
      "chronische Prostatitis Wien",
      "CPPS Beckenschmerzsyndrom",
      "Prostatitis ohne Erregernachweis",
      "Biofilm Prostata qPCR",
      "Blasenbeschwerden Mann komplementär",
    ],
    synonyms: [
      "chronische Prostatitis",
      "CPPS – chronic pelvic pain syndrome",
      "Beckenschmerzen beim Mann",
      "Brennen beim Wasserlassen ohne Befund",
      "häufiger Harndrang trotz negativer Kultur",
      "Schmerzen im Damm oder beim Sitzen",
    ],
    intro:
      "Männer mit chronischen Prostata- und Beckenbeschwerden haben oft eine lange Vorgeschichte: mehrere Antibiotikazyklen, unauffällige Kulturen, die Auskunft, es sei „nichts zu finden“. Dass nichts gefunden wurde, heißt nicht, dass nichts da ist – es heißt zunächst, dass die eingesetzte Methode nichts gezeigt hat.",
    sections: [
      {
        heading: "Vier Kategorien, ein oft gleicher Leidensweg",
        paragraphs: [
          "Die NIH-Klassifikation unterscheidet die akute bakterielle Prostatitis (I), die chronische bakterielle Prostatitis (II), das chronische Beckenschmerzsyndrom mit oder ohne Entzündungszeichen (IIIa/IIIb) und die asymptomatische entzündliche Prostatitis (IV). Der weitaus größte Teil der Betroffenen fällt in Kategorie III – jene Gruppe also, für die es keine einfache antibiotische Antwort gibt.",
          "Klinisch überlappen sich Schmerz im Damm, in Hoden, Penisspitze oder Unterbauch, Miktionsbeschwerden, Schmerzen bei oder nach der Ejakulation sowie eine ausgeprägte Sitzintoleranz. Die Belastung ist erheblich und wird regelmäßig unterschätzt.",
        ],
      },
      {
        heading: "Warum Kulturen zu wenig zeigen",
        paragraphs: [
          "Konventionelle Urin- und Sekretkulturen erfassen nur, was sich unter Standardbedingungen anzüchten lässt. Biofilmassoziierte, intrazelluläre und anspruchsvolle Erreger entziehen sich diesem Verfahren systematisch – und gerade sie sind bei chronisch-rezidivierenden urogenitalen Verläufen relevant. In einem Biofilm sinkt die Stoffwechselaktivität, die Matrix behindert die Penetration von Antibiotika, und die Toleranz gegenüber Wirkstoffen steigt um ein Vielfaches.",
          "Hier setze ich moderne mikrobiologische Diagnostik ein: qPCR und Next-Generation-Sequencing über MicroGenDX erfassen mikrobielle DNA unabhängig von der Kultivierbarkeit und geben zusätzlich Auskunft über Resistenzgene. Das ersetzt keine urologische Abklärung – es ergänzt sie an genau der Stelle, an der sie regelmäßig an ihre Grenze stößt.",
        ],
      },
      {
        heading: "Beckenboden, Nervensystem und Mikrobiom",
        paragraphs: [
          "Bei CPPS ohne Erregernachweis ist häufig ein myofaszialer Anteil führend: chronisch erhöhter Beckenbodentonus mit Triggerpunkten, verstärkt durch Schmerzvermeidung, Sitzhaltung und Anspannung. Dazu kommt eine zentrale Sensibilisierung, die den Schmerz auch nach Abklingen des ursprünglichen Reizes aufrechterhält.",
          "Die Darm-Blasen-Achse spielt mit: eine gestörte Barriere und ein verschobenes Mikrobiom des Urogenitaltrakts halten eine niedriggradige Immunaktivierung aufrecht. Deshalb arbeite ich hier nie nur an einem Organ, sondern an Barriere, Mikrobiota, Entzündungslast, Beckenbodentonus und Stressachse gemeinsam.",
        ],
      },
      {
        heading: "Auch nach langer Vorgeschichte lohnt sich der Versuch",
        paragraphs: [
          "CPPS gilt als schwierig – vor allem, weil es meist nur von einer Seite behandelt wird. Werden mikrobielle, muskuläre und neurologische Anteile gemeinsam bearbeitet, bessern sich Schmerz, Harndrang und Sitzintoleranz in der Mehrzahl der Verläufe deutlich.",
          "Viele Männer erreichen längere beschwerdefreie Phasen, kommen ohne weitere Antibiotikazyklen aus und finden zurück zu Sport, Arbeit und Sexualität ohne ständigen Vorbehalt. Auch nach Jahren ist diese Entwicklung möglich; die Dauer der Vorgeschichte sagt wenig über die Aussichten aus.",
        ],
      },
    ],
    signs: [
      "Schmerz oder Druck im Damm, Hoden, Unterbauch oder in der Penisspitze",
      "häufiger Harndrang, abgeschwächter Strahl oder Restharngefühl",
      "Brennen beim Wasserlassen bei wiederholt negativer Kultur",
      "Beschwerden bei oder nach der Ejakulation",
      "deutliche Verschlechterung durch längeres Sitzen",
      "mehrere Antibiotikazyklen mit nur vorübergehender Besserung",
    ],
    approach: [
      "urologische Abklärung vorausgesetzt – Sichtung von PSA, Sonografie, Restharn und Kulturbefunden",
      "erweiterte mikrobiologische Diagnostik mittels qPCR und NGS über MicroGenDX bei rezidivierenden Verläufen",
      "mikrobiologische Stuhldiagnostik zur Beurteilung von Barriere und Entzündungslast",
      "biofilmwirksame Strategien mit Phytotherapeutika in Arzneimittelqualität, abgestimmt auf den Befund",
      "Mikronährstoff- und Entzündungsmodulation, unter anderem Zink, Vitamin D, Omega-3 und Polyphenole",
      "Empfehlung physiotherapeutischer Beckenbodenarbeit bei myofaszialem Anteil",
      "Arbeit an Stressachse und Schmerzverarbeitung als integralem Teil, nicht als Beiwerk",
    ],
    faq: [
      {
        q: "Ich hatte schon mehrere Antibiotika – warum kommt es immer wieder?",
        a: "Weil ein relevanter Teil chronischer urogenitaler Verläufe biofilmassoziiert ist. Antibiotika erreichen planktonische Keime gut, Bakterien in einer Biofilmmatrix deutlich schlechter. Nach Absetzen kann sich der Biofilm wieder aktivieren – klinisch sieht das wie ein neuer Infekt aus.",
      },
      {
        q: "Was leistet die qPCR- und NGS-Diagnostik konkret?",
        a: "Sie weist mikrobielle DNA nach, unabhängig davon, ob sich der Erreger anzüchten lässt, quantifiziert die gefundenen Spezies und erfasst Resistenzgene. Bei Menschen, bei denen jahrelang „nichts gefunden“ wurde, ist das häufig der erste konkrete Anhaltspunkt.",
      },
      {
        q: "Ersetzt das die Urologie?",
        a: "Nein. PSA-Kontrolle, Ausschluss struktureller Ursachen und Tumorabklärung gehören zwingend in urologische Hand. Ich arbeite ergänzend und stimme mich gerne mit der behandelnden Praxis ab.",
      },
      {
        q: "Ist CPPS psychisch bedingt?",
        a: "Nein. Es handelt sich um ein multifaktorielles Geschehen aus mikrobiellen, muskulären und neurologischen Komponenten. Anhaltender Schmerz verändert allerdings die Schmerzverarbeitung und die psychische Belastbarkeit – das ist Folge, nicht Ursache.",
      },
    ],
    relatedPosts: [
      "interstitielle-zystitis-painful-bladder-syndrome-cpps",
      "die-rolle-von-biofilmen-in-der-symbiose-zwischen-nuetzlichen-und-pathogenen-mikroben",
      "mikrobielles-immunsystem-und-dessen-einfluss-auf-die-immunantwort",
    ],
    serviceAnchor: "#leistungen",
  },
  {
    slug: "biofilme-antibiotikaresistenz",
    title: "Biofilme, pathogene Keime und Antibiotikaresistenz: moderne mikrobiologische Diagnostik",
    shortTitle: "Biofilme & Antibiotikaresistenz",
    metaTitle: "Biofilme & Antibiotikaresistenz: qPCR- und NGS-Diagnostik | Praxis IB Wien",
    metaDescription:
      "Hartnäckige Infekte trotz Antibiotika: Biofilme verstehen, Resistenzgene erfassen, Mikrobiota stärken – qPCR- und NGS-Diagnostik über MicroGenDX in Wien.",
    keywords: [
      "Biofilm Infektion Diagnostik",
      "qPCR NGS Erregernachweis Wien",
      "MicroGenDX Österreich",
      "Antibiotikaresistenz Mikrobiom",
      "chronische Infektion ohne Befund",
    ],
    synonyms: [
      "Biofilm im Körper",
      "Infekt kommt immer wieder",
      "Kultur negativ trotz Beschwerden",
      "multiresistente Keime",
      "Antibiotika wirken nicht mehr",
    ],
    intro:
      "Wenn ein Infekt nach jedem Antibiotikum zurückkehrt, liegt das selten an mangelnder Disziplin und selten an Einbildung. Häufig liegt es an einer Lebensform, die die Standarddiagnostik kaum abbildet: dem Biofilm. Diese Perspektive verändert Diagnostik und Vorgehen grundlegend – und sie gibt vielen Menschen zum ersten Mal eine schlüssige Erklärung.",
    sections: [
      {
        heading: "Was ein Biofilm ist und warum er so widerstandsfähig macht",
        paragraphs: [
          "Der überwiegende Teil aller Bakterien lebt nicht frei schwimmend, sondern in organisierten Gemeinschaften, eingebettet in eine selbst produzierte Matrix aus Polysacchariden, Proteinen und extrazellulärer DNA. Diese Matrix wirkt als Diffusionsbarriere, puffert Sauerstoff- und Nährstoffgradienten und beherbergt sogenannte Persisterzellen mit stark heruntergefahrenem Stoffwechsel.",
          "Da die meisten Antibiotika an aktiven Stoffwechselvorgängen ansetzen, sind solche Zellen ihnen gegenüber bis zu tausendfach toleranter als dieselbe Spezies in planktonischer Form – ohne dass eine klassische genetische Resistenz vorliegen muss. Über Quorum Sensing koordinieren die Bakterien zudem ihr Verhalten und tauschen Resistenzgene über horizontalen Gentransfer aus.",
        ],
      },
      {
        heading: "Warum die Kultur regelmäßig schweigt",
        paragraphs: [
          "Kulturbasierte Verfahren erfassen nur, was unter Laborbedingungen wächst. Anaerobier, intrazelluläre Erreger, langsam wachsende oder biofilmgebundene Keime bleiben unsichtbar. Ein negativer Befund bedeutet daher: mit dieser Methode war nichts nachweisbar – nicht: es ist nichts vorhanden.",
          "qPCR und Next-Generation-Sequencing arbeiten dagegen kulturunabhängig auf DNA-Ebene. Sie identifizieren und quantifizieren Spezies, bilden polymikrobielle Gemeinschaften ab und erfassen zusätzlich Resistenzgene. Für diese Diagnostik arbeite ich mit MicroGenDX zusammen. Bewertet werden die Ergebnisse immer im klinischen Kontext – ein DNA-Nachweis allein ist noch keine Behandlungsindikation.",
        ],
      },
      {
        heading: "Resistenzbildung: was Sie tatsächlich beeinflussen können",
        paragraphs: [
          "Antibiotikaresistenz ist ein globales Problem und zugleich ein sehr individuelles. Jede Therapie verändert die eigene Mikrobiota und selektiert resistente Stämme, die als Reservoir bestehen bleiben. Ein widerstandsfähiges, diverses Mikrobiom ist deshalb nicht nur für die Verdauung relevant, sondern ein aktiver Schutzfaktor: es besetzt ökologische Nischen, konkurriert um Nährstoffe und bildet antimikrobielle Substanzen.",
          "Praktisch bedeutet das: gezielter statt reflexhafter Antibiotikaeinsatz, konsequenter Wiederaufbau danach, und dort, wo es vertretbar ist, biofilmwirksame Strategien mit standardisierten Phytotherapeutika in Arzneimittelqualität. Antibiotika bleiben unverzichtbar – sie sollen nur dann wirken, wenn man sie wirklich braucht.",
        ],
      },
      {
        heading: "Für wen dieser Weg gedacht ist",
        paragraphs: [
          "Für Menschen mit rezidivierenden Harnwegs-, Atemwegs-, Haut- oder Zahn- und Kieferinfekten, mit chronischen urogenitalen Beschwerden ohne Erregernachweis, mit anhaltenden Symptomen nach durchgemachten Infektionen oder mit einer Vorgeschichte vieler Antibiotikazyklen. Nicht jeder braucht diese Diagnostik. Wer sie braucht, erkennt sich meist an einem Satz wieder: „Man hat nie etwas gefunden.“",
        ],
      },
      {
        heading: "Warum diese Perspektive Hoffnung macht",
        paragraphs: [
          "Wer versteht, warum Antibiotika allein nicht ausgereicht haben, hat den entscheidenden Schritt bereits getan. Mit einem klaren Befund und einer Strategie, die Biofilmmatrix, Erregerlast und die eigene Mikrobiota gemeinsam adressiert, durchbrechen viele Menschen einen Kreislauf, der jahrelang unverändert schien.",
          "Realistisch sind längere infektfreie Intervalle, deutlich seltenere Antibiotikagaben und ein Immunsystem, das seine Aufgabe wieder selbst übernimmt. Der Körper ist erstaunlich gut darin, Ordnung herzustellen, sobald er die richtigen Bedingungen bekommt.",
        ],
      },
    ],
    signs: [
      "Infekte, die nach jedem Antibiotikum innerhalb weniger Wochen zurückkehren",
      "anhaltende Beschwerden bei wiederholt negativer Kultur",
      "mehrere Antibiotikazyklen innerhalb eines Jahres",
      "Symptome, die nach einer durchgemachten Infektion nie ganz verschwunden sind",
      "Nachweis multiresistenter Keime in der Vorgeschichte",
      "zunehmende Infektanfälligkeit nach längeren Therapien",
    ],
    approach: [
      "detaillierte Infekt- und Therapieanamnese inklusive aller bisherigen Antibiotikazyklen",
      "kulturunabhängige Erregerdiagnostik mittels qPCR und NGS über MicroGenDX, inklusive Resistenzgenen",
      "mikrobiologische Stuhldiagnostik zur Beurteilung von Diversität, Barriere und Entzündung",
      "Befundinterpretation immer im klinischen Zusammenhang, nicht nach Laborzettel allein",
      "biofilmwirksame Phytotherapie in Arzneimittelqualität, abgestimmt auf Erreger und Verträglichkeit",
      "gezielter Wiederaufbau der Mikrobiota nach antibiotischer Therapie",
      "Rückmeldung und Abstimmung mit der behandelnden ärztlichen Praxis",
    ],
    faq: [
      {
        q: "Ersetzt qPCR/NGS ein Antibiogramm?",
        a: "Nein. Die Verfahren ergänzen einander: Kultur und Antibiogramm zeigen Empfindlichkeiten anzüchtbarer Keime, qPCR und NGS zeigen das gesamte mikrobielle Bild inklusive nicht kultivierbarer Spezies und Resistenzgene. Die Therapieentscheidung fällt im ärztlichen Kontext.",
      },
      {
        q: "Kann man Biofilme auflösen?",
        a: "Vollständige Auflösung ist kein realistisches Versprechen. Es gibt jedoch Substanzen und Strategien, die die Matrix destabilisieren, das Quorum Sensing stören und die Empfindlichkeit gegenüber der eigentlichen Therapie erhöhen. Ziel ist, dem Körper die Kontrolle zurückzugeben, nicht ein Wunder.",
      },
      {
        q: "Führt jeder positive DNA-Nachweis zu einer Behandlung?",
        a: "Nein. Der Mensch ist besiedelt, und das ist gut so. Entscheidend ist die Zusammenschau aus Beschwerdebild, Menge, Spezies und Verlauf. Alles andere wäre Behandlung eines Laborbefunds statt eines Menschen.",
      },
      {
        q: "Wie lange dauert eine solche Begleitung?",
        a: "Nach Anamnese und Diagnostik arbeiten wir in Etappen mit Verlaufskontrollen. Bei chronischen, biofilmassoziierten Verläufen sind drei bis sechs Monate ein realistischer Rahmen, in dem sich Veränderungen belastbar zeigen.",
      },
    ],
    relatedPosts: [
      "die-rolle-von-biofilmen-in-der-symbiose-zwischen-nuetzlichen-und-pathogenen-mikroben",
      "mikrobielles-immunsystem-und-dessen-einfluss-auf-die-immunantwort",
      "interstitielle-zystitis-painful-bladder-syndrome-cpps",
    ],
    serviceAnchor: "#leistungen",
  },
];

const withDeep = (c: Complaint): Complaint => {
  const deep = deepSections[c.slug]?.de;
  return deep ? { ...c, sections: [...c.sections, ...deep] } : c;
};

export const complaints: Complaint[] = [...baseComplaints, ...extraComplaints].map(withDeep);


export const getComplaint = (slug?: string) =>
  complaints.find((c) => c.slug === slug);

/** Alle Themenseiten, die zu einem Blog-Slug passen (für interne Verlinkung im Magazin) */
export const complaintsForPost = (postSlug: string) =>
  complaints.filter((c) => c.relatedPosts.includes(postSlug));
